import { useEffect, useState } from 'react';
import Modal from './Modal';
import { spinnerRing } from '../utils/bookingUtils';
import { useConfig } from '../context/ConfigContext';
import { usePosSession } from '../context/PosSessionContext';
import { savePosClientTheme } from '../utils/posUtil';
import { describeWriteError } from '../utils/firebaseErrorUtil';
import { POS_THEME_FIELDS } from '../constants/posConst';
import { resolveActiveTextColor } from '../utils/themeUtil';
import '../css/Pos.css';

/** A hex colour, in either short or long form. */
const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/**
 * Site Colors — this client's own theme.
 *
 * PER CLIENT, not per deployment. Each club brands its own till, and the
 * values are written to its own subtree so nobody needs an administrator to
 * change their colours. A club that never opens this screen inherits the
 * deployment's palette and then the built-in one.
 *
 * Each field carries its own swatch, so the colour is visible while it is
 * being chosen. The screen itself repaints on SAVE — ConfigContext is
 * subscribed to this node, so the new palette reaches every open till without
 * anyone reloading. Cancel drops unsaved edits by re-reading what is stored.
 *
 * @returns {JSX.Element}
 */
function PosThemeForm() {
  const { config, clientId, loading } = useConfig();
  const { staffEmail } = usePosSession();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);

  // Seeded once from config rather than kept in step with it. Bound live, the
  // field would be rewritten under the cursor by the theme it is editing.
  useEffect(() => {
    if (loading || form) return;
    setForm(POS_THEME_FIELDS.reduce(
      (values, { key }) => ({ ...values, [key]: config[key] || '' }),
      {},
    ));
  }, [loading, form, config]);

  const update = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await savePosClientTheme(clientId, form, staffEmail);
      setModal({
        type: 'success',
        title: 'Theme saved',
        message: 'These colours now apply to everyone at this till.',
      });
    } catch (error) {
      console.error('Saving theme failed:', error);
      setModal({
        type: 'error',
        title: 'Could not save',
        message: describeWriteError(error, {
          what: 'the theme',
          path: `posTenants/${clientId}/config`,
        }),
      });
    } finally {
      setSaving(false);
    }
  };

  /** Drops the unsaved edits by re-seeding from what is stored. @returns {void} */
  const handleReset = () => setForm(null);

  if (loading || !form) {
    return <div className="pos-loading">{spinnerRing('Loading theme…')}</div>;
  }

  return (
    <div className="pos-config">
      <section className="pos-section">
        <div className="pos-section-head">
          <h2>Site Colors</h2>
          <p>
            Controls the colour palette used across this till. Accepts hex values
            (e.g. #6fb5ab). Saved colours apply to everyone here straight away,
            including tills already open.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="pos-theme-grid">
            {POS_THEME_FIELDS.map(({ key, label, hint, optional }) => {
              const value = form[key] || '';
              const valid = HEX_RE.test(value.trim());
              // An optional field left blank is not empty, it is automatic —
              // so the swatch shows the colour that WILL be used rather than a
              // default black that is not what anyone is going to get.
              const auto = optional && !value ? resolveActiveTextColor(form) : '';
              return (
                <div className="pos-field pos-theme-field" key={key}>
                  <span>{label}</span>
                  <span className="pos-theme-hint pos-theme-input-hint">{hint}</span>
                  <div className="pos-theme-input">
                    {/* The native picker and the text field edit one value.
                        The picker is how most people choose a colour; the text
                        field is how someone pastes the club's brand hex out of
                        a style guide, which a picker cannot do. */}
                    <input
                      type="color"
                      className="pos-theme-swatch"
                      value={valid ? value.trim() : (auto || '#000000')}
                      onChange={update(key)}
                      disabled={saving}
                      aria-label={`${label} colour picker`}
                    />
                    <input
                      type="text"
                      className={valid || !value ? '' : 'pos-theme-bad'}
                      value={value}
                      onChange={update(key)}
                      disabled={saving}
                      placeholder={optional ? `Automatic (${auto || 'best contrast'})` : '#6fb5ab'}
                      spellCheck="false"
                      aria-label={label}
                    />
                  </div>
                  {/* Flagged rather than corrected: half-typed "#6fb" is a
                      valid short hex on its way to a long one, so rewriting it
                      would fight whoever is typing. */}
                  {value && !valid && <span className="pos-theme-bad-note">Not a hex colour.</span>}
                  {optional && !value && (
                    <span className="pos-theme-hint">
                      Leave blank and it follows the button colour automatically, picking
                      whichever of black or white can actually be read on it.
                    </span>
                  )}
                  {optional && value && (
                    <button
                      type="button"
                      className="pos-theme-clear"
                      onClick={() => setForm((current) => ({ ...current, [key]: '' }))}
                      disabled={saving}
                    >
                      Back to automatic
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="pos-theme-actions">
            <button type="button" className="pos-btn pos-btn-ghost" onClick={handleReset} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="pos-btn pos-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Configuration'}
            </button>
          </div>
        </form>
      </section>

      <Modal
        isOpen={Boolean(modal)}
        onClose={() => setModal(null)}
        title={modal?.title || ''}
        message={modal?.message || ''}
        type={modal?.type || 'info'}
      />
    </div>
  );
}

export default PosThemeForm;
